const RESPONSE_BODY_KEYS = [
  'statusCode',
  'reason',
  'errorId',
  'message',
  'localizedMessage',
  'id',
]

const truncate = (value, maxLength = 300) => {
  if (value == null) return null

  const text = typeof value === 'string' ? value : JSON.stringify(value)

  if (text.length <= maxLength) return text

  return `${text.slice(0, maxLength)}…`
}

const pickResponseBody = (data) => {
  if (data == null) return null

  const payload =
    typeof data === 'object' &&
    data.data != null &&
    typeof data.data === 'object'
      ? data.data
      : data

  if (typeof payload !== 'object' || payload === null) {
    return truncate(payload)
  }

  const picked = {}

  RESPONSE_BODY_KEYS.forEach((key) => {
    if (payload[key] != null) {
      picked[key] = payload[key]
    }
  })

  return Object.keys(picked).length ? picked : truncate(payload)
}

const pickRequestHeaders = (headers = {}) => ({
  plaidPlatform: headers['plaid-platform'] ?? null,
  plaidStoreId: headers['plaid-store-id'] ?? null,
  plaidVersion: headers['plaid-version'] ?? null,
  xAuthMarker: headers['x-auth-marker'] ?? null,
})

const normalizeApiPath = (apiPath) => {
  if (!apiPath) return '/'

  return apiPath.startsWith('/') ? apiPath : `/${apiPath}`
}

const formatErrorReason = (diagnostics, httpStatus) => {
  if (diagnostics.isTimeout || diagnostics.errorType === 'timeout') {
    return 'timeout'
  }

  if (httpStatus != null) {
    return `HTTP ${httpStatus}`
  }

  if (diagnostics.errorType === 'network') {
    return `network (${diagnostics.axiosCode || 'ERR_NETWORK'})`
  }

  if (diagnostics.errorType === 'cancelled') {
    return 'cancelled'
  }

  return diagnostics.errorType || 'unknown'
}

const buildErrorTexts = ({
  requestConfig,
  diagnostics,
  apiPath,
  httpStatus,
  isWafBlocked,
}) => {
  const path = normalizeApiPath(apiPath)
  const method = (requestConfig.method || 'get').toUpperCase()
  const reason = formatErrorReason(diagnostics, httpStatus)
  const prefix = isWafBlocked ? 'WAF-' : ''

  return {
    message: `${prefix}API ${method} ${path} — ${reason}`,
    name: `${prefix}ApiError ${path} [${reason}]`,
  }
}

/**
 * Собирает компактный payload для Elastic APM без JSON.stringify и axios toJSON().
 * @returns {{ error: Error, labels: object, custom: object }}
 */
export const buildApiApmErrorPayload = ({
  error,
  requestConfig = {},
  diagnostics = {},
  requestUrl = '',
  apiPath = '',
  isWafBlocked = false,
}) => {
  const httpStatus = diagnostics.httpStatus ?? null
  const { message, name } = buildErrorTexts({
    requestConfig,
    diagnostics,
    apiPath,
    httpStatus,
    isWafBlocked,
  })

  const apmError = new Error(message)
  apmError.name = name

  const labels = {
    isOnline: diagnostics.isOnline ?? false,
    netType: String(diagnostics.netType ?? 'unknown'),
    isTimeout: Boolean(diagnostics.isTimeout),
    deviceMemory: diagnostics.deviceMemory ?? 0,
    cpuCores: diagnostics.cpuCores ?? 0,
    httpStatus: httpStatus ?? 0,
    errorType: String(diagnostics.errorType ?? 'unknown'),
  }

  const custom = {
    apiUrl: requestUrl || null,
    apiPath: apiPath || null,
    apiMethod: requestConfig.method ?? null,
    apiType: requestConfig.apiType ?? null,
    locale: requestConfig.params?.locale ?? null,
    axiosCode: diagnostics.axiosCode ?? error?.code ?? null,
    hasResponse: diagnostics.hasResponse ?? false,
    requestHeaders: pickRequestHeaders(requestConfig.headers),
    responseBody: pickResponseBody(error?.response?.data),
    requestBody: truncate(requestConfig.data),
    networkEffectiveType: diagnostics.effectiveType ?? null,
    networkDownlink: diagnostics.downlink ?? null,
    networkRtt: diagnostics.rtt ?? null,
    visibilityState: diagnostics.visibilityState ?? null,
  }

  return { error: apmError, labels, custom }
}
